import { Post } from "@/src/models/post.model";
import { useTheme } from "@/src/styles/theme-provider";
import { getEdgeBackgroundColor } from "@/src/utils/extract-bg-color";
import { useEffect, useState } from "react";
import { Platform } from "react-native";
import { useReactToPost } from "./use-react-to-post";
import { useShareWithPoints } from "./use-share-with-points";
import { useToggleSavePost } from "./use-toggle-save-post";

// `react-native-image-colors` relies on a native module that can be absent in
// some builds (Expo Go / a dev build that hasn't relinked it). Importing it
// statically crashes the whole screen on native, so load it defensively and
// fall back to a plain theme background when it's unavailable. Web is
// unaffected — the module loads normally there.
let ImageColors: any = null;
try {
  ImageColors = require("react-native-image-colors").default;
} catch {
  ImageColors = null;
}

export const usePostCard = (
  post: Post,
  callbacks?: {
    onEdit?: (post: Post) => void;
    onDelete?: (post: Post) => void;
  },
) => {
  const { themeVars, theme } = useTheme();

  // Menu
  const [menuOpen, setMenuOpen] = useState(false);
  const handleMorePress = () => setMenuOpen((prev) => !prev);

  // Viewer
  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Share
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const { handleSharedSuccessfully } = useShareWithPoints();

  // See more/less
  const MAX_LINES = 3;
  /**
   * Pixel line height of the post body, matching the `leading-5` it renders
   * with. The collapsed body is clipped to `MAX_LINES * LINE_HEIGHT` outright,
   * so a browser that lets a clamped line spill past the clamp cannot show a
   * line below the ellipsis.
   */
  const LINE_HEIGHT = 20;
  const [expanded, setExpanded] = useState(false);
  const [showSeeMore, setShowSeeMore] = useState(false);
  const [fullHeight, setFullHeight] = useState(0);

  // Image background color
  const [imageBgColor, setImageBgColor] = useState<string>(themeVars["--color-ash-default"] as string);
  const isDark = theme === "dark";

  useEffect(() => {
    setExpanded(false);
    setShowSeeMore(false);
    setFullHeight(0);
    setImageBgColor(themeVars["--color-ash-default"] as string);
  }, [post.ItemId, themeVars]);

  // Compared against the height three lines occupy, not against the clamped
  // copy's measured height. The clamped copy is the wrong yardstick: whether it
  // reports three lines or the full text depends on how the platform implements
  // the clamp, and when it reported the full height the two were equal and
  // "See more" never appeared under text that was visibly cut. The unclamped
  // height alone answers the question. Two pixels of tolerance for sub-pixel
  // rounding on a post that fills exactly three lines.
  useEffect(() => {
    if (fullHeight > 0) {
      setShowSeeMore(fullHeight > MAX_LINES * LINE_HEIGHT + 2);
    }
  }, [fullHeight]);

const handleImageLoad = async (uri: string) => {
  // Web: sample the image's edges (where the background lives) so a vivid
  // subject — e.g. a yellow logo on black — doesn't win over the real
  // background the way `dominant`/`vibrant` does. Falls through to
  // image-colors when the canvas is CORS-tainted or sampling fails.
  if (Platform.OS === "web") {
    const edgeColor = await getEdgeBackgroundColor(uri);
    if (edgeColor) {
      setImageBgColor(edgeColor);
      return;
    }
  }

  if (!ImageColors?.getColors) {
    setImageBgColor(themeVars["--color-ash-default"] as string);
    return;
  }
  try {
    const result = await ImageColors.getColors(uri, {
      fallback: themeVars["--color-ash-default"] as string,
      cache: true,
      key: uri,
    });

    if (result.platform === "android") {
      setImageBgColor(
        isDark
          ? (result.muted ?? result.darkMuted ?? themeVars["--color-ash-default"] as string)
          : (result.vibrant ?? result.dominant ?? result.muted ?? themeVars["--color-ash-default"] as string),
      );
    } else if (result.platform === "ios") {
      setImageBgColor(
        isDark
          ? (result.background ?? result.secondary ?? themeVars["--color-ash-default"] as string)
          : (result.primary ?? result.detail ?? themeVars["--color-ash-default"] as string),
      );
    } else if (result.platform === "web") {
      setImageBgColor(
        isDark
          ? (result.muted ?? result.darkMuted ?? themeVars["--color-ash-default"] as string)
          : (result.dominant ?? result.vibrant ?? themeVars["--color-ash-default"] as string),
      );
    }
  } catch {
    setImageBgColor(themeVars["--color-ash-default"] as string);
  }
};

  const handleEdit = () => {
    setMenuOpen(false);
    callbacks?.onEdit?.(post);
  };

  const handleDelete = () => {
    setMenuOpen(false);
    callbacks?.onDelete?.(post);
  };

  const { isSaved, handleToggleSave } = useToggleSavePost(post);

  // React to post
  const { userReaction, handleReact, reactionCount } = useReactToPost(post);

  // Media
  const mediaFiles = (post.attachmentUrls ?? []).filter((url) =>
    /\.(jpg|jpeg|png|gif|webp|mp4|mov|avi)$/i.test(url),
  );

  const firstMedia = mediaFiles[0] ? mediaFiles[0] : null;

  const isFirstMediaVideo = /\.(mp4|mov|avi)$/i.test(firstMedia ?? "");

  const totalMediaCount = mediaFiles.length;

  const otherFiles = (post.attachmentUrls ?? []).filter(
    (url) => !/\.(jpg|jpeg|png|gif|webp|mp4|mov|avi)$/i.test(url),
  );

  // Share — award points when share completes
  const handleShareSuccess = () => {
    setShareModalVisible(false);
    handleSharedSuccessfully(post.ItemId);
  };

  return {
    handleEdit,
    handleDelete,

    menuOpen,
    setMenuOpen,
    handleMorePress,

    viewerOpen,
    setViewerOpen,
    selectedIndex,
    setSelectedIndex,

    shareModalVisible,
    setShareModalVisible,
    handleShareSuccess,

    MAX_LINES,
    LINE_HEIGHT,
    expanded,
    setExpanded,
    showSeeMore,
    fullHeight,
    setFullHeight,

    imageBgColor,
    handleImageLoad,

    mediaFiles,
    firstMedia,
    isFirstMediaVideo,
    totalMediaCount,
    otherFiles,

    userReaction,
    handleReact,
    reactionCount,

    isSaved,
    handleToggleSave,
  };
};

